import {
  CarouselProductVariants,
  CarouselSectionTypes,
  CarouselTiledCards,
  ProductCarouselSlide,
} from '@dfh-corp-sites/ui'
import { Subscribe } from '@react-rxjs/core'
import React, { useMemo } from 'react'

import { useResultWithLeftEffect } from '../../../hooks/useResultWithLeftEffect'
import useRetailPrices from '../../../hooks/useRetailPrices'
import { useTestKitPrice } from '../../../hooks/useTestKitPrice'
import { buildCarouselSlideProductData } from '../../../lib/products/carousel'
import { useProductsBySkus } from '../../../lib/products/products-by-skus'
import { constNull } from '../../../utils/constant'
import { pluckData } from '../../../utils/result'
import { CarouselSectionProps } from './types'
import { transformLink } from './utils'

export type CarouselSectionProductProps = Pick<
  CarouselSectionProps,
  'productCarouselDeck' | 'productVariant' | 'title' | 'subtitle' | 'text' | 'ctas'
>

function CarouselSectionProductReactive({
  productCarouselDeck,
  productVariant = CarouselProductVariants.Light,
  title,
  subtitle,
  text,
  ctas,
}: CarouselSectionProductProps) {
  const { shouldDisplayRetailPrice } = useRetailPrices()
  const { shouldShowTestKitPrice } = useTestKitPrice()

  const productSkus =
    productCarouselDeck?.productSlides?.reduce((acc: string[], slide) => {
      const sku = slide.productRef.sku
      if (sku) {
        acc.push(sku)
      }
      return acc
    }, []) ?? []

  const [, data] = useResultWithLeftEffect(useProductsBySkus(...productSkus), pluckData, constNull)

  const slides = useMemo((): ProductCarouselSlide[] => {
    if (!productCarouselDeck?.productSlides?.length) {
      return []
    }

    return productCarouselDeck.productSlides.reduce((acc: ProductCarouselSlide[], slide, index) => {
      const slideType = slide.type ?? CarouselSectionTypes.Product
      if (slide == null || slideType !== CarouselSectionTypes.Product || !slide.productRef?.sku) {
        return acc
      }
      const product = data?.products.find((p) => p.sku === slide.productRef.sku)
      if (!product) return acc

      acc.push({
        ...slide,
        _key: slide._key ?? String(index),
        productData: buildCarouselSlideProductData({
          analyticsEventAppContext: 'featured_products',
          product,
          showPrice: shouldShowTestKitPrice(product.sku),
          showRetailPrice: shouldDisplayRetailPrice,
        }),
        type: slideType,
      })

      return acc
    }, [])
  }, [data, productCarouselDeck?.productSlides, shouldDisplayRetailPrice, shouldShowTestKitPrice])

  const processedCtas =
    productVariant === CarouselProductVariants.Light
      ? ctas?.map((cta, index) => transformLink(cta, String(index)))
      : undefined

  if (slides.length === 0) {
    return null
  }

  return (
    <section className="w-full">
      <CarouselTiledCards
        sliderType={CarouselSectionTypes.Product}
        slides={slides}
        title={title}
        subtitle={subtitle}
        text={text}
        ctas={processedCtas}
        productVariant={productVariant}
      />
    </section>
  )
}

const CarouselSectionProduct: React.FC<CarouselSectionProductProps> = (props) => (
  <Subscribe>
    <CarouselSectionProductReactive {...props} />
  </Subscribe>
)
export default CarouselSectionProduct
